import { Suspense, useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import ErrorBoundary from '@/components/ErrorBoundary';
import ConnectionStatus from '@/components/ConnectionStatus';
import ForceUpdateGate from '@/components/ForceUpdateGate';
import lazyRetry from '@/utils/lazyRetry';

// Componentes críticos - cargan inmediatamente
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';

// Lazy load con retry automático — si falla el import (chunk error por red
// lenta), reintenta 2 veces con backoff antes de rendirse. El ErrorBoundary
// captura la falla final y recarga la página.
const Register = lazyRetry(() => import('@/pages/Register'));
const Profile = lazyRetry(() => import('@/pages/Profile'));
const RechargeBalance = lazyRetry(() => import('@/pages/RechargeBalance'));
const Services = lazyRetry(() => import('@/pages/Services'));
const History = lazyRetry(() => import('@/pages/History'));
const BalanceHistory = lazyRetry(() => import('@/pages/BalanceHistory'));
const Reports = lazyRetry(() => import('@/pages/Reports'));
const AdminPanel = lazyRetry(() => import('@/pages/AdminPanel'));
const AdminConfig = lazyRetry(() => import('@/pages/AdminConfig'));
const PromotionalAdmin = lazyRetry(() => import('@/pages/PromotionalAdmin'));
const ResellerPanel = lazyRetry(() => import('@/pages/ResellerPanel'));
const ProcessorPanel = lazyRetry(() => import('@/pages/ProcessorPanel'));
const WhatsAppPanel = lazyRetry(() => import('@/pages/WhatsAppPanel'));
const MyCommissions = lazyRetry(() => import('@/pages/MyCommissions'));

// Componentes flotantes - lazy load con retry
const WhatsAppSupport = lazyRetry(() => import('@/components/WhatsAppSupport'));
const FloatingMessages = lazyRetry(() => import('@/components/FloatingMessages'));
const PopupNotification = lazyRetry(() => import('@/components/PopupNotification'));
const InstallPWA = lazyRetry(() => import('@/components/InstallPWA'));

// Loading spinner para lazy components
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      <span className="text-sm text-slate-500">Cargando...</span>
    </div>
  </div>
);

// Mini loader para componentes flotantes
const MiniLoader = () => null;

// Componente para redirigir al landing page estático
const LandingRedirect = () => {
  // Redirigir al archivo landing.html estático
  window.location.href = '/landing.html';
  return <PageLoader />;
};

// True cuando estamos dentro del shell nativo Android (WebAppActivity inyecta
// el sufijo "RecargameYaApp/" en el UA + el objeto window.AndroidPrinter).
// En ese contexto NO queremos redirigir al landing.html estático — la app
// debe ir directo al login si no hay sesión.
const isNativeShell = () => {
  if (typeof navigator === 'undefined') return false;
  if (typeof window !== 'undefined' && window.AndroidPrinter) return true;
  return /RecargameYaApp\//.test(navigator.userAgent || '');
};

// Ruta raíz que respeta el estado de carga del AuthContext antes de decidir
// hacia dónde redirigir. Sin este guardia, `user` es null mientras se
// hidrata la sesión y disparamos LandingRedirect por error → los usuarios
// que ya estaban logueados terminaban viendo landing.html tras cada reinicio
// de la app (bug reportado en el shell Android nativo).
const homeForRole = (user) => {
  if (!user) return '/login';
  if (user.role === 'remittance_processor') return '/procesador-remesas';
  return '/dashboard';
};

const RootRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user) return <Navigate to={homeForRole(user)} replace />;
  if (isNativeShell()) return <Navigate to="/login" replace />;
  return <LandingRedirect />;
};

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!user) return <Navigate to="/login" />;
  // Remittance processors have a locked-down panel — force redirect from
  // any other route to /procesador-remesas.
  if (user.role === 'remittance_processor') {
    return <Navigate to="/procesador-remesas" replace />;
  }
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  return user && (user.role === 'admin' || user.role === 'sub_admin') ? children : <Navigate to="/dashboard" />;
};

const ProcessorRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'remittance_processor') return <Navigate to="/dashboard" />;
  return children;
};

const ResellerRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  return user && user.role === 'revendedor' ? children : <Navigate to="/dashboard" />;
};

function AppContent() {
  const { user } = useAuth();
  const isProcessor = user?.role === 'remittance_processor';
  const isApiClient = user?.role === 'api_client';
  
  return (
    <div className="App min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <Suspense fallback={<PageLoader />}>
        <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/login" element={user ? <Navigate to={homeForRole(user)} /> : <Login />} />
            <Route path="/register" element={user ? <Navigate to={homeForRole(user)} /> : <Register />} />
            <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/recharge-balance" element={<PrivateRoute><RechargeBalance /></PrivateRoute>} />
            <Route path="/services" element={<PrivateRoute><Services /></PrivateRoute>} />
            <Route path="/history" element={<PrivateRoute><History /></PrivateRoute>} />
            <Route path="/balance-history" element={<PrivateRoute><BalanceHistory /></PrivateRoute>} />
            <Route path="/mis-comisiones" element={<PrivateRoute><MyCommissions /></PrivateRoute>} />
            <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
            <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />
            <Route path="/admin/config" element={<AdminRoute><AdminConfig /></AdminRoute>} />
            <Route path="/admin/promotional" element={<AdminRoute><PromotionalAdmin /></AdminRoute>} />
            <Route path="/admin/whatsapp" element={<AdminRoute><WhatsAppPanel /></AdminRoute>} />
            <Route path="/reseller" element={<ResellerRoute><ResellerPanel /></ResellerRoute>} />
            <Route path="/procesador-remesas" element={<ProcessorRoute><ProcessorPanel /></ProcessorRoute>} />
          </Routes>
        </Suspense>
        <Toaster position="top-center" expand={false} richColors closeButton />
        <ConnectionStatus />
        {!isProcessor && !isApiClient && (
          <Suspense fallback={<MiniLoader />}>
            <FloatingMessages />
            <PopupNotification />
            <WhatsAppSupport />
            {user && <InstallPWA />}
          </Suspense>
        )}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ForceUpdateGate>
            <AppContent />
          </ForceUpdateGate>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
